import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LoadingButton from '../LoadingButton';
import partnerApiService from '../../services/partnerApi';
import { formatCredits } from '../../utils/credits';

// SOLICITAR AMPLIACIÓN DEL TECHO mayorista.
//
// Lo que se pide aquí NO son créditos: es LÍNEA. El partner compra créditos él
// mismo cuando quiere (PurchaseCreditsDialog); lo que no puede hacer solo es
// subirse el techo, porque es la exposición que el marketplace le acepta antes
// de facturar. Aprobar esto no le carga saldo — le sube el límite hasta el que
// podrá comprar.
//
// El copy del diálogo insiste en esa distinción a propósito: es el punto donde
// un partner podría creer que ha pedido créditos y quedarse esperando un saldo
// que nunca va a aparecer solo.

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  // Techo y saldo actuales (de /partner/me): el techo actual es el suelo de lo
  // que se puede pedir, y el saldo explica por qué se le ha quedado corto.
  currentMax?: number | string | null;
  wholesaleBalance?: number | string | null;
}

const LimitRequestDialog: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  currentMax,
  wholesaleBalance
}) => {
  const { t } = useTranslation();
  const [requested, setRequested] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cap = Number(currentMax ?? 0);
  const hasCap = cap > 0;

  useEffect(() => {
    if (!open) return;
    // Sugerencia de salida: el doble del techo actual. Es un punto de partida
    // razonable y editable — mejor que un campo vacío que obliga a pensar en
    // frío cuál es «el número correcto».
    setRequested(hasCap ? String(cap * 2) : '');
    setNote('');
    setError('');
  }, [open, cap, hasCap]);

  const amount = Number(requested);
  const valid =
    requested.trim() !== '' && !Number.isNaN(amount) && amount > 0 && (!hasCap || amount > cap);

  const handleSubmit = async () => {
    if (!valid) return;
    try {
      setLoading(true);
      setError('');
      await partnerApiService.createLimitRequest({
        requested_max_credits: amount,
        note: note.trim() || undefined
      });
      toast.success(t('partner.limitRequest.sent', 'Solicitud enviada: el marketplace la revisará'));
      onClose();
      onCreated?.();
    } catch (err: any) {
      console.error('Failed to create limit request:', err);
      setError(err?.detail || t('partner.limitRequest.error', 'No se pudo enviar la solicitud'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('partner.limitRequest.title', 'Solicitar ampliación de techo')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {/* La distinción crédito / línea, en el sitio donde importa. */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('partner.limitRequest.hint', 'No estás pidiendo créditos: los compras tú cuando quieras. Lo que pides es subir el techo hasta el que puedes comprar. Te avisaremos por correo con la decisión.')}
        </Alert>
        {!hasCap ? (
          <Alert severity="success">
            {t('partner.limitRequest.noCap', 'Tu bolsa no tiene techo: puedes comprar los créditos que necesites sin pedir nada.')}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('partner.limitRequest.current', 'Tu techo hoy: {{cap}} créditos · en tu bolsa tienes {{balance}}', {
                cap: formatCredits(cap),
                balance: formatCredits(wholesaleBalance ?? 0)
              })}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              type="number"
              fullWidth
              variant="outlined"
              label={t('partner.limitRequest.requested', 'Techo que necesitas (créditos)') + ' *'}
              value={requested}
              onChange={(e) => setRequested(e.target.value)}
              inputProps={{ min: 1, step: '1' }}
              error={requested !== '' && !valid}
              helperText={
                requested !== '' && !valid
                  ? t('partner.limitRequest.mustBeHigher', 'Pide un techo mayor que el actual ({{cap}})', {
                      cap: formatCredits(cap)
                    })
                  : undefined
              }
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              label={t('partner.limitRequest.note', 'Mensaje para el marketplace')}
              placeholder={t('partner.limitRequest.notePlaceholder', 'Ej.: entran tres clientes nuevos este mes y me quedo sin margen.')}
              helperText={t('partner.limitRequest.noteHelp', 'Cuanto más claro sea el motivo, más rápido se aprueba.')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'Cancelar')}
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          disabled={!valid || !hasCap}
          variant="contained"
        >
          {t('partner.limitRequest.submit', 'Enviar solicitud')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default LimitRequestDialog;
